import type { DateRange, DateValue } from 'reka-ui';

export const useRangeCalendarStartModel = (
  model: Ref<DateRange | undefined>,
  isSelecting: Ref<boolean>,
  oneWay: Ref<boolean>,
  onSelectionStart: () => void,
  onSelectionEnd: () => void,
  scheduleClose: () => void,
) =>
  computed<DateRange>({
    get: () => ({
      // Inverted: existingEnd becomes the reka-ui anchor so hover preview shows range [hover → end]
      start: isSelecting.value ? model.value?.end : model.value?.start,
      end: isSelecting.value ? undefined : model.value?.end,
    }),
    set: (v) => {
      if (v.end && isSelecting.value) {
        applyInvertedAnchorClick(v, model, onSelectionEnd, scheduleClose);
      } else if (v.end) {
        model.value = { start: v.start ?? model.value?.start, end: v.end };
      } else if (!isSelecting.value && v.start && model.value?.end) {
        if (model.value.start && v.start.compare(model.value.start) === 0) {
          scheduleClose();
        } else if (v.start.compare(model.value.end) >= 0) {
          model.value = { start: v.start, end: undefined };
        } else {
          model.value = { start: v.start, end: model.value.end };
        }
      } else if (oneWay.value && v.start) {
        model.value = { start: v.start, end: undefined };
      } else if (!model.value?.end && v.start) {
        // No end date yet: set start directly without going through the inverted-anchor flow
        model.value = { start: v.start, end: undefined };
      } else {
        onSelectionStart();
      }
    },
  });

export const useRangeCalendarOneWayModel = (model: Ref<DateRange | undefined>) =>
  computed<DateValue | undefined>({
    get: () => model.value?.start,
    set: (v) => {
      model.value = { start: v, end: undefined };
    },
  });

export const useRangeCalendarEndModel = (
  model: Ref<DateRange | undefined>,
  isSelecting: Ref<boolean>,
  scheduleClose: () => void,
  onSelectionStart: () => void,
  onSelectionEnd: () => void,
) =>
  computed<DateRange>({
    get: () => ({
      start: model.value?.start,
      end: isSelecting.value ? undefined : model.value?.end,
    }),
    set: (v) => {
      if (v.end) {
        model.value = { start: model.value?.start ?? v.start, end: v.end };
        onSelectionEnd();
        scheduleClose();
      } else if (!isSelecting.value && v.start) {
        // reka-ui resets the range on click; treat the clicked date as the new end.
        if (model.value?.start && v.start.compare(model.value.start) === 0) return;
        model.value = { start: model.value?.start, end: v.start };
        scheduleClose();
      } else {
        onSelectionStart();
      }
    },
  });

// Reka normalizes click on X to {start: min(X,anchor), end: max(X,anchor)}; v.start===anchor means X>anchor
const applyInvertedAnchorClick = (
  v: DateRange,
  model: Ref<DateRange | undefined>,
  onSelectionEnd: () => void,
  scheduleClose: () => void,
): void => {
  onSelectionEnd();
  const anchor = model.value?.end;
  if (!anchor || !v.start || !v.end) return;
  if (v.start.compare(anchor) === 0) {
    if (v.end.compare(anchor) !== 0) model.value = { start: v.end, end: undefined };
  } else if (model.value?.start && v.start.compare(model.value.start) === 0) {
    scheduleClose();
  } else {
    model.value = { start: v.start, end: anchor };
  }
};
